import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { AuthFacade } from '@core/facades/auth.facade';
import { MemberFacade } from '@core/facades/member.facade';
import { TermsConfigService } from '@core/services/terms-config.service';
import { resolveDefaultLandingRoute } from '@shared/utils/default-landing-route';
import { rememberRedirectIntent } from '@shared/utils/redirect-intent';
import { resolveReturnUrl } from '@shared/utils/return-url';
import { PreviewSessionService } from '@core/services/preview-session.service';

export const termsGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthFacade);
  const memberFacade = inject(MemberFacade);
  const termsConfig = inject(TermsConfigService);
  const router = inject(Router);

  // A read-only task preview is anonymous: there is no member and no terms to
  // accept, and the terms-config endpoint is not served in preview. Skip the
  // whole check so it never fires a request that would 403.
  if (inject(PreviewSessionService).isActive()) {
    return true;
  }

  return combineLatest([
    memberFacade.member$,
    termsConfig.configState$,
  ]).pipe(
    filter(([, configState]) => configState.status !== 'loading'),
    take(1),
    map(([member, configState]) => {
      const currentVersion = configState.data?.currentTermsVersion ?? null;
      const returnUrl = resolveReturnUrl(
        state.url,
        resolveDefaultLandingRoute(auth.getRoleValue()),
      );

      if (!member || !currentVersion) {
        return true;
      }

      if (member.termsVersionAccepted !== currentVersion) {
        rememberRedirectIntent(returnUrl);

        return router.createUrlTree(['/terms'], {
          queryParams: { returnUrl },
        });
      }

      if (
        !state.url.startsWith('/contact-onboarding') &&
        member.contactOnboardingRequiredAt &&
        !member.contactOnboardingCompletedAt
      ) {
        rememberRedirectIntent(returnUrl);

        return router.createUrlTree(['/contact-onboarding'], {
          queryParams: { returnUrl },
        });
      }

      return true;
    }),
    catchError(() => of(true)),
  );
};
